/**
 * "Configure deployment" modal (Figma node 8569:63217).
 *
 * Used for Docker download, ZIP (raw model files) download, AND live deploy.
 * The ``artifactType`` prop ('docker' | 'zip') controls the downloaded-file
 * name field label/extension ('.tar' vs '.zip'). Both formats bundle all
 * selected tasks into one artifact, so the single name field always applies
 * when downloading.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Divider,
  Input,
  Modal,
  Select,
  Tooltip,
} from 'antd';
import { CloseOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { ReadOnlyGuard } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import {
  CLOUD_PROVIDER_OPTIONS,
  HARDWARE_DEVICE_OPTIONS,
  getAcceleratorOptions,
  getInstanceTypeOptions,
} from '@/features/evaluation/api/performanceMetricsApi';
import {
  DEPLOYMENT_INFRA_CATALOG,
  USE_DB_DEPLOYMENT_INFRA_CATALOG,
  resolveDeploymentInfraCatalog,
} from './configureDeployment/deploymentInfraCatalog';
import ObjectiveTabs from '@/features/tasks/components/Task/ObjectiveTabs';
import '@/app/styles/scrollbar.css';
import '@/shared/styles/AntTabsFullWidthTabs.css';
import './ConfigureDeploymentModal.css';
import TaskRuntimeConfigPanel from './configureDeployment/TaskRuntimeConfigPanel';
import {
  filterEligibleTrainings,
  buildTaskSelectOptions,
  buildVersionSelectOptions,
  findLatestTrainingForUsecase,
  findTrainingForUsecaseVersion,
} from './configureDeployment/eligibleTrainings';
import { buildTaskKey } from './configureDeployment/taskKeys';
import { createSelectedTask, createTaskRuntimeConfig } from './configureDeployment/taskRuntimeDefaults';
import { trainingToTabUsecase } from './configureDeployment/trainingToTabUsecase';
import {
  buildDefaultDockerImageName,
  buildDockerOutputFilename,
  deploymentDeviceFromHardware,
  normalizeDockerImageName,
  validateDockerImageName,
} from './configureDeployment/dockerImageName';
import { isNerTaskType, isVllmTaskType } from '@/features/trainings/constants';

function firstOrNull(arr) {
  return Array.isArray(arr) && arr.length ? arr[0] : null;
}

function pickFirstInstance(catalog, cloud, hardware, accelerator) {
  const opts = getInstanceTypeOptions(cloud, hardware, accelerator, catalog);
  return firstOrNull(opts);
}

function pickFirstSelection(catalog) {
  const cloud = firstOrNull(Object.keys(catalog || {}));
  if (!cloud) return null;
  const hardware = firstOrNull(Object.keys(catalog[cloud] || {}));
  if (!hardware) return null;
  const accelerators = getAcceleratorOptions(cloud, hardware, catalog);
  const accelerator = accelerators[0]?.value ?? null;
  const instance = pickFirstInstance(catalog, cloud, hardware, accelerator);
  return { cloud, hardware, accelerator, instance };
}

function findInstanceMeta(catalog, cloud, hardware, accelerator, instanceType) {
  const opts = getInstanceTypeOptions(cloud, hardware, accelerator, catalog);
  return opts.find((o) => o.value === instanceType) || null;
}

function sumInstanceGroupCount(selectedTasks) {
  return selectedTasks.reduce((total, entry) => {
    if (isVllmTaskType(entry.training?.task_type)) return total;
    return total + (entry.runtimeConfig?.instance_group_count ?? 0);
  }, 0);
}

const REGISTRY_ALIAS_ORDER = { dev: 0, prod: 1 };

function sortRegistryTargetsByAlias(targets) {
  return [...(targets || [])].sort(
    (a, b) =>
      (REGISTRY_ALIAS_ORDER[a?.alias] ?? 99) - (REGISTRY_ALIAS_ORDER[b?.alias] ?? 99)
  );
}

function capitalizeAlias(alias) {
  const value = alias || 'dev';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatEcrTargetLabel(target) {
  const mode = target.account_type === 'cross_account' ? 'cross-account' : 'same-account';
  return `${capitalizeAlias(target.alias)} (${mode})`;
}

function formatGcrTargetLabel(target) {
  const mode = target.auth_mode === 'cross_project' ? 'cross-project' : 'same-project';
  return `${capitalizeAlias(target.alias)} (${mode})`;
}

function formatAcrTargetLabel(target) {
  const mode =
    target.auth_mode === 'cross_subscription' ? 'cross-subscription' : 'same-subscription';
  return `${capitalizeAlias(target.alias)} (${mode})`;
}

function formatDockerHubTargetLabel(target) {
  return capitalizeAlias(target.alias);
}

function FieldLabel({ children, tooltip }) {
  return (
    <div className="configure-deployment-modal__label">
      <span>{children}</span>
      {tooltip ? (
        <Tooltip title={tooltip}>
          <QuestionCircleOutlined className="configure-deployment-modal__label-icon" />
        </Tooltip>
      ) : null}
    </div>
  );
}

function SectionHeading({ title, description, tooltip }) {
  return (
    <div className="configure-deployment-modal__section-heading">
      <div className="configure-deployment-modal__section-title-row">
        <h3 className="configure-deployment-modal__section-title">{title}</h3>
        {tooltip ? (
          <Tooltip title={tooltip}>
            <QuestionCircleOutlined className="configure-deployment-modal__label-icon" />
          </Tooltip>
        ) : null}
      </div>
      {description ? (
        <p className="configure-deployment-modal__section-desc">{description}</p>
      ) : null}
    </div>
  );
}

function InfrastructureSection({
  catalog,
  catalogLoading,
  cloudProvider,
  hardwareDevice,
  accelerator,
  instanceType,
  onChange,
}) {
  const cloudOptions = useMemo(() => {
    const liveCloudKeys = Object.keys(catalog || {});
    if (!liveCloudKeys.length) return CLOUD_PROVIDER_OPTIONS;
    return liveCloudKeys.map((value) => {
      const match = CLOUD_PROVIDER_OPTIONS.find((o) => o.value === value);
      return match || { value, label: value };
    });
  }, [catalog]);

  const hardwareOptions = useMemo(() => {
    const liveHwKeys = Object.keys(catalog?.[cloudProvider] || {});
    if (!liveHwKeys.length) return HARDWARE_DEVICE_OPTIONS;
    return liveHwKeys.map((value) => {
      const match = HARDWARE_DEVICE_OPTIONS.find((o) => o.value === value);
      return match || { value, label: value };
    });
  }, [catalog, cloudProvider]);

  const acceleratorOptions = useMemo(
    () => getAcceleratorOptions(cloudProvider, hardwareDevice, catalog),
    [catalog, cloudProvider, hardwareDevice]
  );

  const instanceOptions = useMemo(
    () => getInstanceTypeOptions(cloudProvider, hardwareDevice, accelerator, catalog),
    [catalog, cloudProvider, hardwareDevice, accelerator]
  );

  const handleCloud = (value) => {
    const hardware = firstOrNull(Object.keys(catalog?.[value] || {}));
    const accelerators = getAcceleratorOptions(value, hardware, catalog);
    const nextAcc = accelerators[0]?.value ?? null;
    const nextInstanceOpt = pickFirstInstance(catalog, value, hardware, nextAcc);
    onChange({
      cloudProvider: value,
      hardwareDevice: hardware,
      accelerator: nextAcc,
      instanceType: nextInstanceOpt?.value || null,
      instanceMeta: nextInstanceOpt,
    });
  };

  const handleHardware = (value) => {
    const accelerators = getAcceleratorOptions(cloudProvider, value, catalog);
    const nextAcc = accelerators[0]?.value ?? null;
    const nextInstanceOpt = pickFirstInstance(catalog, cloudProvider, value, nextAcc);
    onChange({
      cloudProvider,
      hardwareDevice: value,
      accelerator: nextAcc,
      instanceType: nextInstanceOpt?.value || null,
      instanceMeta: nextInstanceOpt,
    });
  };

  const handleAccelerator = (value) => {
    const nextInstanceOpt = pickFirstInstance(
      catalog,
      cloudProvider,
      hardwareDevice,
      value
    );
    onChange({
      cloudProvider,
      hardwareDevice,
      accelerator: value,
      instanceType: nextInstanceOpt?.value || null,
      instanceMeta: nextInstanceOpt,
    });
  };

  const handleInstance = (value) => {
    const meta = findInstanceMeta(
      catalog,
      cloudProvider,
      hardwareDevice,
      accelerator,
      value
    );
    onChange({
      cloudProvider,
      hardwareDevice,
      accelerator,
      instanceType: value,
      instanceMeta: meta,
    });
  };

  return (
    <div className="configure-deployment-modal__section">
      <SectionHeading
        title="Infrastructure"
        description="Defaults are preselected based on recommended performance and cost / on your previous selection."
      />
      <div className="configure-deployment-modal__infra-grid">
        <div>
          <FieldLabel tooltip="Cloud where the model will run">Cloud provider</FieldLabel>
          <Select
            loading={catalogLoading}
            value={cloudProvider}
            onChange={handleCloud}
            options={cloudOptions}
          />
        </div>
        <div>
          <FieldLabel tooltip="CPU or GPU hardware">Hardware device</FieldLabel>
          <Select
            loading={catalogLoading}
            value={hardwareDevice}
            onChange={handleHardware}
            options={hardwareOptions}
          />
        </div>
        <div>
          <FieldLabel tooltip="GPU accelerator type, if applicable">Accelerator</FieldLabel>
          <Select
            loading={catalogLoading}
            value={accelerator ?? null}
            onChange={handleAccelerator}
            options={acceleratorOptions}
            placeholder={acceleratorOptions.length ? undefined : '(none)'}
            disabled={!acceleratorOptions.length}
          />
        </div>
        <div>
          <FieldLabel tooltip="Machine instance type">Instance type</FieldLabel>
          <Select
            loading={catalogLoading}
            value={instanceType}
            onChange={handleInstance}
            options={instanceOptions}
            disabled={!instanceOptions.length}
          />
        </div>
      </div>
    </div>
  );
}

export default function ConfigureDeploymentModal({
  open,
  mode,
  training,
  artifactType = 'docker',
  availableTrainings = [],
  allUsecases = [],
  instancesCapErrorByTaskKey = {},
  focusedTaskKey = null,
  onClearInstancesCapErrorForTask,
  onFocusedTaskKeyChange,
  onCancel,
  onBack,
  onConfirm,
}) {
  const [catalog, setCatalog] = useState(DEPLOYMENT_INFRA_CATALOG);
  const [catalogLoading, setCatalogLoading] = useState(false);
  // True while the parent's onConfirm promise (download / deploy request) is
  // in flight — drives the Done button's loading spinner so the click has
  // visible feedback until the parent closes the modal.
  const [submitting, setSubmitting] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [activeTaskKey, setActiveTaskKey] = useState(null);
  const [localCapErrorByTaskKey, setLocalCapErrorByTaskKey] = useState({});
  const [dockerImageName, setDockerImageName] = useState('');
  const [dockerImageNameTouched, setDockerImageNameTouched] = useState(false);
  const [dockerImageNameError, setDockerImageNameError] = useState(null);
  // Toggled true when the operator clicks Done with at least one Triton
  // task missing a Prediction strategy. Cleared as soon as they pick one
  // (see ``updateTaskRuntimeConfig``) so the message disappears the
  // moment the offending field is fixed.
  const [predictionStrategyMissing, setPredictionStrategyMissing] = useState(false);

  const [cloudProvider, setCloudProvider] = useState(null);
  const [hardwareDevice, setHardwareDevice] = useState(null);
  const [accelerator, setAccelerator] = useState(null);
  const [instanceType, setInstanceType] = useState(null);
  const [instanceMeta, setInstanceMeta] = useState(null);
  const [preferredInfrastructure, setPreferredInfrastructure] = useState(false);

  // ECR "Push to ECR" target. The repository URIs are pre-configured by a
  // DevOps user in Account Settings and tagged with a prod/dev alias; the
  // operator just picks one by alias here. The concrete URI + account mode
  // are resolved from the chosen target and sent to the backend.
  const [ecrTargets, setEcrTargets] = useState([]);
  const [ecrTargetsLoading, setEcrTargetsLoading] = useState(false);
  const [ecrSelectedIndex, setEcrSelectedIndex] = useState(null);
  const [ecrTargetTouched, setEcrTargetTouched] = useState(false);

  // GCR / Artifact Registry targets (same alias pattern as ECR).
  const [gcrTargets, setGcrTargets] = useState([]);
  const [gcrTargetsLoading, setGcrTargetsLoading] = useState(false);
  const [gcrSelectedIndex, setGcrSelectedIndex] = useState(null);
  const [gcrTargetTouched, setGcrTargetTouched] = useState(false);

  // Docker Hub targets (same alias pattern as ECR / GCR).
  const [dockerhubTargets, setDockerhubTargets] = useState([]);
  const [dockerhubTargetsLoading, setDockerhubTargetsLoading] = useState(false);
  const [dockerhubSelectedIndex, setDockerhubSelectedIndex] = useState(null);
  const [dockerhubTargetTouched, setDockerhubTargetTouched] = useState(false);

  // Azure ACR targets (same alias pattern as ECR / GCR / Docker Hub).
  const [acrTargets, setAcrTargets] = useState([]);
  const [acrTargetsLoading, setAcrTargetsLoading] = useState(false);
  const [acrSelectedIndex, setAcrSelectedIndex] = useState(null);
  const [acrTargetTouched, setAcrTargetTouched] = useState(false);

  const getUsecaseById = useCallback(
    (usecaseId) => allUsecases.find((u) => u._id === usecaseId) || null,
    [allUsecases]
  );

  const primaryTraining = selectedTasks[0]?.training || training;
  const baseModelName = primaryTraining?.base_model_name || null;
  const isVllmFlow = isVllmTaskType(primaryTraining?.task_type);

  // Restrict the task picker to one runtime family at a time: a single
  // Docker image / deployment cannot mix vLLM-served and Triton-served
  // tasks, so we filter ``availableTrainings`` by the primary training's
  // runtime bucket before it flows into the picker / Add Task logic.
  const filteredAvailableTrainings = useMemo(
    () =>
      (availableTrainings || []).filter(
        (row) => isVllmTaskType(row?.task_type) === isVllmFlow
      ),
    [availableTrainings, isVllmFlow]
  );

  const eligibleToAdd = useMemo(
    () => filterEligibleTrainings(filteredAvailableTrainings, mode, selectedTasks),
    [filteredAvailableTrainings, mode, selectedTasks]
  );

  const deploymentDevice = useMemo(
    () => deploymentDeviceFromHardware(hardwareDevice),
    [hardwareDevice]
  );

  // Both formats bundle all selected tasks into ONE artifact (Docker image /
  // tar, or a single ZIP of raw files), so the single user-entered file name
  // always applies when downloading.
  const isZipArtifact = artifactType === 'zip';
  const isEcrArtifact = artifactType === 'ecr';
  const isGcrArtifact = artifactType === 'gcr';
  const isDockerHubArtifact = artifactType === 'dockerhub';
  const isAcrArtifact = artifactType === 'acr';
  const isRegistryPushArtifact =
    isEcrArtifact || isGcrArtifact || isDockerHubArtifact || isAcrArtifact;
  const downloadExtension = isZipArtifact ? '.zip' : '.tar';
  // Registry push has no local file, so the docker/zip name field is hidden
  // and replaced by the target section below.
  const showNameField = mode === 'download' && !isRegistryPushArtifact;
  const dockerhubCloudBlocked =
    isDockerHubArtifact &&
    ['gcp', 'google', 'azure'].includes((cloudProvider || '').toLowerCase());

  const suggestedDockerImageName = useMemo(
    () => buildDefaultDockerImageName(selectedTasks, { hardwareDevice }),
    [selectedTasks, hardwareDevice]
  );

  const trainingsPool = useMemo(() => {
    const byKey = new Map();
    [...filteredAvailableTrainings, ...selectedTasks.map((entry) => entry.training)].forEach(
      (row) => {
        if (row?.llm_usecase_id != null && row.version != null) {
          byKey.set(buildTaskKey(row), row);
        }
      }
    );
    return Array.from(byKey.values());
  }, [filteredAvailableTrainings, selectedTasks]);

  const tabUsecases = useMemo(
    () =>
      selectedTasks.map((entry) =>
        trainingToTabUsecase(entry.training, getUsecaseById(entry.training.llm_usecase_id))
      ),
    [selectedTasks, getUsecaseById]
  );

  const taskKeyByUsecaseId = useMemo(() => {
    const map = {};
    selectedTasks.forEach((entry) => {
      map[entry.training.llm_usecase_id] = entry.taskKey;
    });
    return map;
  }, [selectedTasks]);

  const hasCapErrorForTask = useCallback(
    (taskKey) =>
      Boolean(localCapErrorByTaskKey[taskKey] || instancesCapErrorByTaskKey[taskKey]),
    [localCapErrorByTaskKey, instancesCapErrorByTaskKey]
  );

  const errorByUsecaseId = useMemo(() => {
    const out = {};
    selectedTasks.forEach((entry) => {
      if (hasCapErrorForTask(entry.taskKey) && entry.training?.llm_usecase_id) {
        out[entry.training.llm_usecase_id] = true;
      }
    });
    return out;
  }, [selectedTasks, hasCapErrorForTask]);

  const effectiveActiveTaskKey =
    focusedTaskKey ||
    activeTaskKey ||
    selectedTasks[0]?.taskKey ||
    null;

  const effectiveActiveUsecaseId = useMemo(() => {
    const entry = selectedTasks.find((t) => t.taskKey === effectiveActiveTaskKey);
    return entry?.training?.llm_usecase_id || tabUsecases[0]?._id || null;
  }, [selectedTasks, effectiveActiveTaskKey, tabUsecases]);

  useEffect(() => {
    if (!open) return undefined;
    if (!USE_DB_DEPLOYMENT_INFRA_CATALOG) {
      setCatalog(DEPLOYMENT_INFRA_CATALOG);
      setCatalogLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCatalogLoading(true);
    resolveDeploymentInfraCatalog({ baseModelName })
      .then((liveCatalog) => {
        if (!cancelled) setCatalog(liveCatalog);
      })
      .catch(() => {
        if (!cancelled) setCatalog(DEPLOYMENT_INFRA_CATALOG);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, baseModelName]);

  // Load the DevOps-configured ECR targets so the operator can pick one
  // by alias. Only fetched for the ECR push flow.
  useEffect(() => {
    if (!open || artifactType !== 'ecr') return undefined;
    let cancelled = false;
    setEcrTargetsLoading(true);
    data_from_server_post(
      'get_organization_settings',
      {},
      (result) => {
        if (cancelled) return;
        const targets = Array.isArray(result?.aws_ecr_targets)
          ? result.aws_ecr_targets.filter((t) => t && t.uri)
          : [];
        setEcrTargets(targets);
        setEcrSelectedIndex(targets.length === 1 ? 0 : null);
        setEcrTargetsLoading(false);
      },
      () => {
        if (cancelled) return;
        setEcrTargets([]);
        setEcrTargetsLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [open, artifactType]);

  // Load GCR / Artifact Registry targets for the Push to GCR flow.
  useEffect(() => {
    if (!open || artifactType !== 'gcr') return undefined;
    let cancelled = false;
    setGcrTargetsLoading(true);
    data_from_server_post(
      'get_organization_settings',
      {},
      (result) => {
        if (cancelled) return;
        const targets = Array.isArray(result?.gcp_gcr_targets)
          ? result.gcp_gcr_targets.filter((t) => t && t.uri)
          : [];
        setGcrTargets(targets);
        setGcrSelectedIndex(targets.length === 1 ? 0 : null);
        setGcrTargetsLoading(false);
      },
      () => {
        if (cancelled) return;
        setGcrTargets([]);
        setGcrTargetsLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [open, artifactType]);

  // Load Docker Hub targets for the Push to Docker Hub flow.
  useEffect(() => {
    if (!open || artifactType !== 'dockerhub') return undefined;
    let cancelled = false;
    setDockerhubTargetsLoading(true);
    data_from_server_post(
      'get_organization_settings',
      {},
      (result) => {
        if (cancelled) return;
        const targets = Array.isArray(result?.dockerhub_targets)
          ? result.dockerhub_targets.filter((t) => t && t.repository)
          : [];
        setDockerhubTargets(targets);
        setDockerhubSelectedIndex(targets.length === 1 ? 0 : null);
        setDockerhubTargetsLoading(false);
      },
      () => {
        if (cancelled) return;
        setDockerhubTargets([]);
        setDockerhubTargetsLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [open, artifactType]);

  // Load Azure ACR targets for the Push to Azure ACR flow.
  useEffect(() => {
    if (!open || artifactType !== 'acr') return undefined;
    let cancelled = false;
    setAcrTargetsLoading(true);
    data_from_server_post(
      'get_organization_settings',
      {},
      (result) => {
        if (cancelled) return;
        const targets = Array.isArray(result?.azure_acr_targets)
          ? result.azure_acr_targets.filter((t) => t && t.uri)
          : [];
        setAcrTargets(targets);
        setAcrSelectedIndex(targets.length === 1 ? 0 : null);
        setAcrTargetsLoading(false);
      },
      () => {
        if (cancelled) return;
        setAcrTargets([]);
        setAcrTargetsLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [open, artifactType]);

  useEffect(() => {
    if (!open || !training) return;
    const usecase = getUsecaseById(training.llm_usecase_id);
    const seedTask = createSelectedTask(training, usecase);
    setSelectedTasks([seedTask]);
    setActiveTaskKey(seedTask.taskKey);
    setLocalCapErrorByTaskKey({});
    setDockerImageNameTouched(false);
    setDockerImageNameError(null);
    setEcrSelectedIndex(null);
    setEcrTargetTouched(false);
    setGcrSelectedIndex(null);
    setGcrTargetTouched(false);
    setDockerhubSelectedIndex(null);
    setDockerhubTargetTouched(false);
    setAcrSelectedIndex(null);
    setAcrTargetTouched(false);

    const preferredConfig =
      usecase?.preferred_deployment_config ||
      training?.usecase_preferred_deployment_config ||
      null;
    const seedInfra = preferredConfig
      ? {
        cloud: preferredConfig.cloud_provider,
        hardware: preferredConfig.hardware_device,
        accelerator: preferredConfig.accelerator,
        instance: preferredConfig.instance_type
          ? {
            value: preferredConfig.instance_type,
            label: preferredConfig.instance_type,
          }
          : null,
      }
      : pickFirstSelection(catalog);
    if (seedInfra) {
      setCloudProvider(seedInfra.cloud);
      setHardwareDevice(seedInfra.hardware);
      setAccelerator(seedInfra.accelerator);
      setInstanceType(seedInfra.instance?.value || null);
      setInstanceMeta(
        seedInfra.instance ||
        findInstanceMeta(
          catalog,
          seedInfra.cloud,
          seedInfra.hardware,
          seedInfra.accelerator,
          seedInfra.instance?.value
        )
      );
    }
    setPreferredInfrastructure(Boolean(preferredConfig));
  }, [open, training, catalog, getUsecaseById]);

  useEffect(() => {
    if (!open || mode !== 'download' || dockerImageNameTouched) return;
    setDockerImageName(suggestedDockerImageName);
    setDockerImageNameError(null);
  }, [open, mode, suggestedDockerImageName, dockerImageNameTouched, hardwareDevice]);

  useEffect(() => {
    if (focusedTaskKey) {
      setActiveTaskKey(focusedTaskKey);
    }
  }, [focusedTaskKey]);

  const maxInstances = instanceMeta?.maxInstances ?? null;

  const totalInstanceCount = useMemo(
    () => sumInstanceGroupCount(selectedTasks),
    [selectedTasks]
  );

  const clearCapErrorForTask = useCallback(
    (taskKey) => {
      setLocalCapErrorByTaskKey((prev) => {
        if (!prev[taskKey]) return prev;
        const next = { ...prev };
        delete next[taskKey];
        return next;
      });
      onClearInstancesCapErrorForTask?.(taskKey);
    },
    [onClearInstancesCapErrorForTask]
  );

  const clearAllCapErrors = useCallback(() => {
    setLocalCapErrorByTaskKey({});
    selectedTasks.forEach((entry) => {
      onClearInstancesCapErrorForTask?.(entry.taskKey);
    });
  }, [selectedTasks, onClearInstancesCapErrorForTask]);

  const handleInfrastructureChange = (next) => {
    setCloudProvider(next.cloudProvider);
    setHardwareDevice(next.hardwareDevice);
    setAccelerator(next.accelerator);
    setInstanceType(next.instanceType);
    setInstanceMeta(next.instanceMeta);
    clearAllCapErrors();
  };

  const handleTabChange = (usecaseId) => {
    const taskKey = taskKeyByUsecaseId[usecaseId];
    if (taskKey) {
      setActiveTaskKey(taskKey);
      onFocusedTaskKeyChange?.(null);
    }
  };

  const handleAddTask = () => {
    const row = eligibleToAdd[0];
    if (!row) return;
    const usecase = getUsecaseById(row.llm_usecase_id);
    const entry = createSelectedTask(row, usecase);
    setSelectedTasks((prev) => [...prev, entry]);
    setActiveTaskKey(entry.taskKey);
    onFocusedTaskKeyChange?.(null);
  };

  const handleTaskSelect = (taskKey, usecaseId) => {
    const current = selectedTasks.find((entry) => entry.taskKey === taskKey);
    if (!current || current.training.llm_usecase_id === usecaseId) return;

    const nextTraining = findLatestTrainingForUsecase(
      trainingsPool,
      mode,
      usecaseId
    );
    if (!nextTraining) return;

    const usecase = getUsecaseById(usecaseId);
    const nextEntry = createSelectedTask(nextTraining, usecase);
    setSelectedTasks((prev) =>
      prev.map((entry) => (entry.taskKey === taskKey ? nextEntry : entry))
    );
    clearCapErrorForTask(taskKey);
    if (effectiveActiveTaskKey === taskKey) {
      setActiveTaskKey(nextEntry.taskKey);
    }
  };

  const handleVersionSelect = (taskKey, version, usecaseId) => {
    const nextTraining = findTrainingForUsecaseVersion(
      trainingsPool,
      usecaseId,
      version
    );
    if (!nextTraining) return;

    const usecase = getUsecaseById(usecaseId);
    const prior = selectedTasks.find((entry) => entry.taskKey === taskKey);
    const nextEntry = {
      ...createSelectedTask(nextTraining, usecase),
      runtimeConfig:
        prior?.runtimeConfig ||
        createTaskRuntimeConfig(nextTraining, usecase),
    };

    setSelectedTasks((prev) =>
      prev.map((entry) => (entry.taskKey === taskKey ? nextEntry : entry))
    );
    clearCapErrorForTask(taskKey);
    if (effectiveActiveTaskKey === taskKey) {
      setActiveTaskKey(nextEntry.taskKey);
    }
  };

  const handleRemoveTask = (taskKey) => {
    if (selectedTasks.length <= 1) return;
    setSelectedTasks((prev) => prev.filter((t) => t.taskKey !== taskKey));
    clearCapErrorForTask(taskKey);
    if (effectiveActiveTaskKey === taskKey) {
      const remaining = selectedTasks.filter((t) => t.taskKey !== taskKey);
      setActiveTaskKey(remaining[0]?.taskKey || null);
    }
  };

  const updateTaskRuntimeConfig = useCallback((taskKey, runtimeConfig) => {
    setSelectedTasks((prev) =>
      prev.map((entry) =>
        entry.taskKey === taskKey
          ? { ...entry, runtimeConfig }
          : entry
      )
    );
    // Picking *any* Prediction strategy on any task clears the footer
    // error: the validation only fires on missing values, and we don't
    // want a stale red message lingering after the user fixed it.
    if (runtimeConfig?.prediction_strategy) {
      setPredictionStrategyMissing(false);
    }
  }, []);

  // Catch the indirect fix path too: if the operator removes the
  // offending task instead of picking a strategy on it, the error must
  // also disappear.
  useEffect(() => {
    if (!predictionStrategyMissing) return;
    const stillMissing = selectedTasks.some(
      (entry) =>
        !isVllmTaskType(entry.training?.task_type) &&
        !isNerTaskType(entry.training?.task_type) &&
        !entry.runtimeConfig?.prediction_strategy
    );
    if (!stillMissing) {
      setPredictionStrategyMissing(false);
    }
  }, [selectedTasks, predictionStrategyMissing]);

  const getCapErrorMessage = (taskKey) => {
    const external = instancesCapErrorByTaskKey[taskKey];
    if (typeof external === 'string' && external) {
      return external;
    }
    if (
      localCapErrorByTaskKey[taskKey] &&
      typeof maxInstances === 'number' &&
      totalInstanceCount > maxInstances &&
      instanceType
    ) {
      return `Up to ${maxInstances} instances on ${instanceType}`;
    }
    return null;
  };

  const dockerNameValidationError = showNameField
    ? validateDockerImageName(dockerImageName, downloadExtension)
    : null;

  const selectedEcrTarget =
    isEcrArtifact && ecrSelectedIndex != null ? ecrTargets[ecrSelectedIndex] : null;
  const ecrTargetValidationError =
    isEcrArtifact && !selectedEcrTarget ? 'Select an ECR target' : null;

  const selectedGcrTarget =
    isGcrArtifact && gcrSelectedIndex != null ? gcrTargets[gcrSelectedIndex] : null;
  const gcrTargetValidationError =
    isGcrArtifact && !selectedGcrTarget ? 'Select a GCR target' : null;

  const selectedDockerHubTarget =
    isDockerHubArtifact && dockerhubSelectedIndex != null
      ? dockerhubTargets[dockerhubSelectedIndex]
      : null;
  const dockerhubTargetValidationError =
    isDockerHubArtifact && !selectedDockerHubTarget
      ? 'Select a Docker Hub target'
      : null;
  const dockerhubCloudValidationError = dockerhubCloudBlocked
    ? 'Docker Hub push is not available for GCP/Azure in this version. Select AWS.'
    : null;

  const selectedAcrTarget =
    isAcrArtifact && acrSelectedIndex != null ? acrTargets[acrSelectedIndex] : null;
  const acrTargetValidationError =
    isAcrArtifact && !selectedAcrTarget ? 'Select an ACR target' : null;

  const isValid =
    selectedTasks.length > 0 &&
    !!cloudProvider &&
    !!instanceType &&
    selectedTasks.every(
      (entry) => entry.runtimeConfig.instance_group_count >= 1
    ) &&
    !dockerNameValidationError &&
    !ecrTargetValidationError &&
    !gcrTargetValidationError &&
    !dockerhubTargetValidationError &&
    !dockerhubCloudValidationError &&
    !acrTargetValidationError;

  const handleConfirm = async () => {
    if (submitting) return;
    if (showNameField) {
      const nameError = validateDockerImageName(dockerImageName, downloadExtension);
      if (nameError) {
        setDockerImageNameError(nameError);
        return;
      }
    }
    if (isEcrArtifact && !selectedEcrTarget) {
      setEcrTargetTouched(true);
      return;
    }
    if (isGcrArtifact && !selectedGcrTarget) {
      setGcrTargetTouched(true);
      return;
    }
    if (isDockerHubArtifact && dockerhubCloudBlocked) {
      return;
    }
    if (isDockerHubArtifact && !selectedDockerHubTarget) {
      setDockerhubTargetTouched(true);
      return;
    }
    if (isAcrArtifact && !selectedAcrTarget) {
      setAcrTargetTouched(true);
      return;
    }
    // Classification tasks must always carry a chosen Prediction strategy.
    // vLLM (summarization) and NER tasks legitimately have
    // ``prediction_strategy === null`` (their runtimes ignore it / the
    // control is hidden), so they're skipped. Surfacing this on Done —
    // rather than disabling the button — gives the operator a clear,
    // scannable reason for why the click didn't work.
    const missingPredictionStrategy = selectedTasks.some(
      (entry) =>
        !isVllmTaskType(entry.training?.task_type) &&
        !isNerTaskType(entry.training?.task_type) &&
        !entry.runtimeConfig?.prediction_strategy
    );
    if (missingPredictionStrategy) {
      setPredictionStrategyMissing(true);
      return;
    }
    if (!isValid) return;

    const sharedConfig = {
      cloud_provider: cloudProvider,
      hardware_device: hardwareDevice,
      accelerator: accelerator || null,
      instance_type: instanceType,
      device: (hardwareDevice || '').toLowerCase() === 'cpu' ? 'cpu' : 'gpu',
      preferred_infrastructure: preferredInfrastructure,
      ...(isEcrArtifact && selectedEcrTarget
        ? {
          ecr_repository_uri: (selectedEcrTarget.uri || '').trim(),
          ecr_account_mode: selectedEcrTarget.account_type || 'same_account',
          ecr_alias: selectedEcrTarget.alias || null,
        }
        : {}),
      ...(isGcrArtifact && selectedGcrTarget
        ? {
          gcr_repository_uri: (selectedGcrTarget.uri || '').trim(),
          gcr_auth_mode: selectedGcrTarget.auth_mode || 'same_project',
          gcr_alias: selectedGcrTarget.alias || null,
        }
        : {}),
      ...(isDockerHubArtifact && selectedDockerHubTarget
        ? {
          dockerhub_repository: (selectedDockerHubTarget.repository || '').trim(),
          dockerhub_alias: selectedDockerHubTarget.alias || null,
        }
        : {}),
      ...(isAcrArtifact && selectedAcrTarget
        ? {
          acr_repository_uri: (selectedAcrTarget.uri || '').trim(),
          acr_auth_mode: selectedAcrTarget.auth_mode || 'same_subscription',
          acr_alias: selectedAcrTarget.alias || null,
        }
        : {}),
    };

    const overCap =
      typeof maxInstances === 'number' && totalInstanceCount > maxInstances;
    if (overCap && instanceType) {
      setLocalCapErrorByTaskKey(
        Object.fromEntries(selectedTasks.map((entry) => [entry.taskKey, true]))
      );
      setActiveTaskKey(selectedTasks[0].taskKey);
      onFocusedTaskKeyChange?.(selectedTasks[0].taskKey);
      return;
    }

    const normalizedDockerName = normalizeDockerImageName(
      dockerImageName,
      downloadExtension
    );
    // Only emit a shared output_filename when a single name field is shown
    // (Docker, or single-task ZIP). Multi-task ZIP omits it so the backend
    // names each per-model zip from its own default.
    const downloadOutputFilename =
      showNameField
        ? buildDockerOutputFilename(
          normalizedDockerName,
          selectedTasks[0].training,
          selectedTasks[0].runtimeConfig,
          downloadExtension
        )
        : null;

    setSubmitting(true);
    try {
      await onConfirm({
        mode,
        artifactType,
        sharedConfig: {
          ...sharedConfig,
          ...(downloadOutputFilename
            ? { output_filename: downloadOutputFilename }
            : {}),
        },
        dockerImageName: showNameField ? normalizedDockerName : null,
        tasks: selectedTasks.map((entry) => ({
          training: entry.training,
          config: { ...entry.runtimeConfig },
        })),
      });
    } finally {
      // The parent closes the modal on success; on validation errors
      // (instances cap, duplicate ECR push) it keeps the modal open, so
      // always drop the spinner once the promise settles.
      setSubmitting(false);
    }
  };

  const renderRuntimePanel = useCallback(
    (usecase) => {
      const taskKey = taskKeyByUsecaseId[usecase._id];
      const entry = selectedTasks.find((t) => t.taskKey === taskKey);
      if (!entry) return null;

      return (
        <TaskRuntimeConfigPanel
          training={entry.training}
          runtimeConfig={entry.runtimeConfig}
          onRuntimeConfigChange={(runtimeConfig) =>
            updateTaskRuntimeConfig(taskKey, runtimeConfig)
          }
          instancesCapErrorMessage={getCapErrorMessage(taskKey)}
          onInstancesFieldChange={clearAllCapErrors}
        />
      );
    },
    [
      selectedTasks,
      taskKeyByUsecaseId,
      updateTaskRuntimeConfig,
      localCapErrorByTaskKey,
      instancesCapErrorByTaskKey,
      maxInstances,
      totalInstanceCount,
      instanceType,
      clearAllCapErrors,
    ]
  );

  const doneButton = (
    <Button
      type="primary"
      className="configure-deployment-modal__done-btn"
      onClick={handleConfirm}
      disabled={!isValid}
      loading={submitting}
    >
      {isEcrArtifact
        ? 'Push to ECR'
        : isGcrArtifact
          ? 'Push to GCR'
          : isDockerHubArtifact
            ? 'Push to Docker Hub'
            : 'Done'}
    </Button>
  );

  const modalFooter = (
    <div className="configure-deployment-modal__footer-inner">
      <p className="configure-deployment-modal__footer-note">
        Configuration changes apply to both deployment and download packages.
      </p>
      <div className="configure-deployment-modal__footer-actions">
        {predictionStrategyMissing ? (
          <p
            className="configure-deployment-modal__footer-error"
            role="alert"
          >
            Select a Prediction strategy for every task to continue.
          </p>
        ) : null}
        <div className="configure-deployment-modal__footer-buttons">
          <Button
            onClick={mode === 'download' && onBack ? onBack : onCancel}
            disabled={submitting}
          >
            {mode === 'download' && onBack ? 'Back' : 'Cancel'}
          </Button>
          {mode === 'download' && !isRegistryPushArtifact
            ? doneButton
            : <ReadOnlyGuard>{doneButton}</ReadOnlyGuard>}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      className="configure-deployment-modal"
      open={open}
      onCancel={onCancel}
      footer={modalFooter}
      destroyOnClose
      width={860}
      zIndex={2100}
      title={
        <div>
          <h2 className="configure-deployment-modal__title">Configure deployment</h2>
          <p className="configure-deployment-modal__subtitle">
            Select the tasks to include, then choose how each task should run.
          </p>
        </div>
      }
      centered
    >
      <div className="configure-deployment-modal__scroll optune-scrollbar">
        <div className="configure-deployment-modal__section">
          <div className="configure-deployment-modal__task-block">
            {selectedTasks.map((entry, index) => {
              const usecaseId = entry.training.llm_usecase_id;
              const isFirstRow = index === 0;
              const taskOptions = buildTaskSelectOptions(
                trainingsPool,
                mode,
                selectedTasks,
                usecaseId
              );
              const versionOptions = buildVersionSelectOptions(
                trainingsPool,
                mode,
                usecaseId,
                entry.training.version
              );

              return (
                <div
                  key={entry.taskKey}
                  className="configure-deployment-modal__task-row"
                >
                  <div className="configure-deployment-modal__task-fields-wrap">
                    <div className="configure-deployment-modal__task-fields">
                      <div className="configure-deployment-modal__task-field">
                        <FieldLabel>Select Task</FieldLabel>
                        <Select
                          showSearch
                          value={usecaseId}
                          onChange={(value) => handleTaskSelect(entry.taskKey, value)}
                          options={taskOptions}
                          optionFilterProp="label"
                        />
                      </div>
                      <div className="configure-deployment-modal__task-field">
                        <FieldLabel>Select Version</FieldLabel>
                        <Select
                          value={entry.training.version}
                          onChange={(value) =>
                            handleVersionSelect(entry.taskKey, value, usecaseId)
                          }
                          options={versionOptions}
                        />
                      </div>
                    </div>
                    {isFirstRow ? (
                      <p className="configure-deployment-modal__version-hint">
                        Only one version per task can be selected.
                      </p>
                    ) : null}
                  </div>
                  {isFirstRow ? (
                    !isVllmFlow ? (
                      <div className="configure-deployment-modal__add-task-wrap">
                        <Button
                          icon={<PlusOutlined />}
                          onClick={handleAddTask}
                          disabled={!eligibleToAdd.length}
                        >
                          Add Task
                        </Button>
                      </div>
                    ) : null
                  ) : (
                    <div className="configure-deployment-modal__add-task-wrap">
                      <Button
                        type="text"
                        icon={<CloseOutlined />}
                        aria-label="Remove task"
                        onClick={() => handleRemoveTask(entry.taskKey)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <InfrastructureSection
          catalog={catalog}
          catalogLoading={catalogLoading}
          cloudProvider={cloudProvider}
          hardwareDevice={hardwareDevice}
          accelerator={accelerator}
          instanceType={instanceType}
          onChange={handleInfrastructureChange}
        />

        {isVllmFlow ? null : <Divider className="configure-deployment-modal__divider" />}

        {selectedTasks.length > 1 ? (
          <div className="configure-deployment-modal__task-tabs">
            <ObjectiveTabs
              usecases={tabUsecases}
              activeUsecaseId={effectiveActiveUsecaseId}
              onChange={handleTabChange}
              errorByUsecaseId={errorByUsecaseId}
              renderPanel={renderRuntimePanel}
            />
          </div>
        ) : (
          selectedTasks[0] ? (
            renderRuntimePanel(tabUsecases[0])
          ) : null
        )}

        {showNameField ? (
          <>
            <Divider className="configure-deployment-modal__divider" />
            <div className="configure-deployment-modal__section configure-deployment-modal__docker-name-section">
              <SectionHeading
                title={isZipArtifact ? 'File name' : 'Docker image name'}
                description={
                  deploymentDevice
                    ? `Infrastructure: ${deploymentDevice === 'cpu' ? 'CPU' : 'GPU'} — suggested name ends with _${deploymentDevice} (Triton instance group matches this target).`
                    : 'Select infrastructure above to include _cpu or _gpu in the suggested file name.'
                }
                tooltip={
                  isZipArtifact
                    ? 'File name used when you save the raw model files (.zip) to your computer. The suggested name includes _cpu or _gpu so it matches your infrastructure selection.'
                    : 'File name used when you save the Docker image to your computer. The suggested name includes _cpu or _gpu so it matches your infrastructure selection.'
                }
              />
              <Input
                value={dockerImageName}
                onChange={(event) => {
                  setDockerImageNameTouched(true);
                  setDockerImageName(event.target.value);
                  if (dockerImageNameError) {
                    setDockerImageNameError(
                      validateDockerImageName(event.target.value, downloadExtension)
                    );
                  }
                }}
                onBlur={() => {
                  setDockerImageNameError(
                    validateDockerImageName(dockerImageName, downloadExtension)
                  );
                }}
                status={dockerImageNameError ? 'error' : undefined}
                placeholder={suggestedDockerImageName}
              />
              {dockerImageNameError ? (
                <p className="configure-deployment-modal__field-error">
                  {dockerImageNameError}
                </p>
              ) : (
                <p className="configure-deployment-modal__field-hint">
                  Saved as{' '}
                  {normalizeDockerImageName(dockerImageName, downloadExtension) ||
                    suggestedDockerImageName}
                  {downloadExtension}
                  {selectedTasks.length > 1
                    ? isZipArtifact
                      ? ' (one ZIP with all selected models)'
                      : ' (one image with all selected models)'
                    : ''}
                </p>
              )}
            </div>
          </>
        ) : null}

        {isEcrArtifact ? (
          <>
            <Divider className="configure-deployment-modal__divider" />
            <div className="configure-deployment-modal__section">
              <SectionHeading
                title="ECR target"
                description="The inference image is built and pushed to the selected ECR repository. The image tag is set automatically to {task type}-{project name}-{task name}-v{version}."
                tooltip="Targets are configured by your DevOps team in Account Settings and selected here by environment alias."
              />
              <FieldLabel tooltip="Pre-configured ECR destination, identified by its prod/dev alias.">
                Environment
              </FieldLabel>
              <Select
                style={{ width: '100%' }}
                loading={ecrTargetsLoading}
                value={ecrSelectedIndex}
                onChange={(value) => {
                  setEcrTargetTouched(true);
                  setEcrSelectedIndex(value);
                }}
                placeholder="Select an ECR target"
                status={ecrTargetValidationError && ecrTargetTouched ? 'error' : undefined}
                options={ecrTargets.map((target, index) => ({
                  value: index,
                  label: target.alias || 'dev',
                }))}
                notFoundContent={
                  ecrTargetsLoading ? 'Loading…' : 'No ECR targets configured'
                }
              />
              {!ecrTargetsLoading && ecrTargets.length === 0 ? (
                <p className="configure-deployment-modal__field-hint">
                  No ECR targets configured. Ask a DevOps admin to add one under
                  Account Settings → ECR push targets.
                </p>
              ) : selectedEcrTarget ? (
                <p className="configure-deployment-modal__field-hint">
                  Target: {selectedEcrTarget.alias || 'dev'}
                  {' '}({selectedEcrTarget.account_type === 'cross_account'
                    ? 'cross-account'
                    : 'same-account'}). Tag: {'{task-type}-{project-name}-{task-name}-v{version}'}.
                </p>
              ) : ecrTargetValidationError && ecrTargetTouched ? (
                <p className="configure-deployment-modal__field-error">
                  {ecrTargetValidationError}
                </p>
              ) : null}
            </div>
          </>
        ) : null}

        {isGcrArtifact ? (
          <>
            <Divider className="configure-deployment-modal__divider" />
            <div className="configure-deployment-modal__section">
              <SectionHeading
                title="GCR target"
                description="The inference image is built and pushed to the selected GCP Artifact Registry / GCR repository. The image tag is set automatically to {task type}-{project name}-{task name}-v{version}."
                tooltip="Targets are configured by your DevOps team in Account Settings (GCP - GCR) and selected here by environment alias."
              />
              <FieldLabel tooltip="Pre-configured GCR destination, identified by its prod/dev alias.">
                Environment
              </FieldLabel>
              <Select
                style={{ width: '100%' }}
                loading={gcrTargetsLoading}
                value={gcrSelectedIndex}
                onChange={(value) => {
                  setGcrTargetTouched(true);
                  setGcrSelectedIndex(value);
                }}
                placeholder="Select a GCR target"
                status={gcrTargetValidationError && gcrTargetTouched ? 'error' : undefined}
                options={gcrTargets.map((target, index) => ({
                  value: index,
                  label: target.alias || 'dev',
                }))}
                notFoundContent={
                  gcrTargetsLoading ? 'Loading…' : 'No GCR targets configured'
                }
              />
              {!gcrTargetsLoading && gcrTargets.length === 0 ? (
                <p className="configure-deployment-modal__field-hint">
                  No GCR targets configured. Ask a DevOps admin to add one under
                  Account Settings → External Deploy - Targets (GCP - GCR).
                </p>
              ) : selectedGcrTarget ? (
                <p className="configure-deployment-modal__field-hint">
                  Target: {selectedGcrTarget.alias || 'dev'}
                  {' '}({selectedGcrTarget.auth_mode === 'service_account'
                    ? 'service-account'
                    : 'same-project'}). Tag: {'{task-type}-{project-name}-{task-name}-v{version}'}.
                </p>
              ) : gcrTargetValidationError && gcrTargetTouched ? (
                <p className="configure-deployment-modal__field-error">
                  {gcrTargetValidationError}
                </p>
              ) : null}
            </div>
          </>
        ) : null}

        {isDockerHubArtifact ? (
          <>
            <Divider className="configure-deployment-modal__divider" />
            <div className="configure-deployment-modal__section">
              <SectionHeading
                title="Docker Hub target"
                description="The inference image is built and pushed to the selected Docker Hub repository. The image tag is set automatically to {task type}-{project name}-{task name}-v{version}."
                tooltip="Targets are configured by your DevOps team in Account Settings and selected here by environment alias."
              />
              {dockerhubCloudValidationError ? (
                <p className="configure-deployment-modal__field-error">
                  {dockerhubCloudValidationError}
                </p>
              ) : null}
              <FieldLabel tooltip="Pre-configured Docker Hub destination, identified by its prod/dev alias.">
                Environment
              </FieldLabel>
              <Select
                style={{ width: '100%' }}
                loading={dockerhubTargetsLoading}
                value={dockerhubSelectedIndex}
                onChange={(value) => {
                  setDockerhubTargetTouched(true);
                  setDockerhubSelectedIndex(value);
                }}
                placeholder="Select a Docker Hub target"
                status={
                  dockerhubTargetValidationError && dockerhubTargetTouched
                    ? 'error'
                    : undefined
                }
                disabled={Boolean(dockerhubCloudValidationError)}
                options={dockerhubTargets.map((target, index) => ({
                  value: index,
                  label: target.alias || 'dev',
                }))}
                notFoundContent={
                  dockerhubTargetsLoading
                    ? 'Loading…'
                    : 'No Docker Hub targets configured'
                }
              />
              {!dockerhubTargetsLoading && dockerhubTargets.length === 0 ? (
                <p className="configure-deployment-modal__field-hint">
                  No Docker Hub targets configured. Ask a DevOps admin to add one under
                  Account Settings → External Deploy - Targets (Docker Hub).
                </p>
              ) : selectedDockerHubTarget ? (
                <p className="configure-deployment-modal__field-hint">
                  Target: {selectedDockerHubTarget.alias || 'dev'} (
                  {selectedDockerHubTarget.repository}). Tag:{' '}
                  {'{task-type}-{project-name}-{task-name}-v{version}'}.
                </p>
              ) : dockerhubTargetValidationError && dockerhubTargetTouched ? (
                <p className="configure-deployment-modal__field-error">
                  {dockerhubTargetValidationError}
                </p>
              ) : null}
            </div>
          </>
        ) : null}

        {isAcrArtifact ? (
          <>
            <Divider className="configure-deployment-modal__divider" />
            <div className="configure-deployment-modal__section">
              <SectionHeading
                title="Azure ACR target"
                description="The inference image is built and pushed to the selected Azure Container Registry repository. The image tag is set automatically to {task type}-{project name}-{task name}-v{version}. Auth uses the Service Principal configured in Account Settings."
                tooltip="Targets are configured by your DevOps team in Account Settings (Azure - ACR) and selected here by environment alias."
              />
              <FieldLabel tooltip="Pre-configured ACR destination, identified by its prod/dev alias.">
                Environment
              </FieldLabel>
              <Select
                style={{ width: '100%' }}
                loading={acrTargetsLoading}
                value={acrSelectedIndex}
                onChange={(value) => {
                  setAcrTargetTouched(true);
                  setAcrSelectedIndex(value);
                }}
                placeholder="Select an ACR target"
                status={acrTargetValidationError && acrTargetTouched ? 'error' : undefined}
                options={acrTargets.map((target, index) => ({
                  value: index,
                  label: target.alias || 'dev',
                }))}
                notFoundContent={
                  acrTargetsLoading ? 'Loading…' : 'No ACR targets configured'
                }
              />
              {!acrTargetsLoading && acrTargets.length === 0 ? (
                <p className="configure-deployment-modal__field-hint">
                  No ACR targets configured. Ask a DevOps admin to add one under
                  Account Settings → External Deploy - Targets (Azure - ACR).
                </p>
              ) : selectedAcrTarget ? (
                <p className="configure-deployment-modal__field-hint">
                  Target: {selectedAcrTarget.alias || 'dev'} (service-principal).
                  Tag: {'{task-type}-{project-name}-{task-name}-v{version}'}.
                </p>
              ) : acrTargetValidationError && acrTargetTouched ? (
                <p className="configure-deployment-modal__field-error">
                  {acrTargetValidationError}
                </p>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
